import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '../../../components/ui';
import { activeFilter } from '../../../components/common/ListPage/listFilters';
import { Cell, ListPage } from '../../../components/common/ListPage/ListPage';
import { cn } from '../../../utils/cn';
import { money } from '../../../utils/currency';
import { masterOptions } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import type { Item } from '../types/materialTypes';

const isLow = (i) => (i.currentStock ?? 0) <= (i.minStockLevel ?? 0);

export function StockQty({ item }: { item: Item }) {
  return (
    <span className={cn('tabular font-medium', isLow(item) ? 'text-red-600' : 'text-navy-900')}>
      {item.currentStock ?? 0} {item.unit}
    </span>
  );
}

export default function MaterialList() {
  const navigate = useNavigate();
  useMasters();
  const categoryFilter = { key: 'category', label: 'Category', icon: 'grid', options: masterOptions('materialCategory') };
  const add = <Button leftIcon="plus" onClick={() => navigate('/materials/new')}>Add material</Button>;
  return (
    <ListPage
      title="Materials"
      subtitle="Stock items: wire, switches, MCBs, fans, lights"
      path="/items"
      baseQuery={{ type: 'material' }}
      exportName="materials"
      filters={[categoryFilter, { key: 'lowStock', label: 'Stock', icon: 'layers', options: [{ value: 'true', label: 'Low stock only' }] }, activeFilter]}
      searchPlaceholder="Name, code, brand"
      actions={
        <>
          <Button variant="outline" leftIcon="layers" onClick={() => navigate('/stock/adjust')}>Adjust stock</Button>
          {add}
        </>
      }
      rowTo={(i) => `/materials/${i._id}`}
      rowActions={(i) =>
        [
          { label: 'View', icon: 'eye', onClick: () => navigate(`/materials/${i._id}`) },
          { label: 'Edit', icon: 'edit', onClick: () => navigate(`/materials/${i._id}/edit`) },
          { label: 'Adjust stock', icon: 'layers', onClick: () => navigate(`/stock/adjust?itemId=${i._id}`) },
          isLow(i) && { label: 'Buy more', icon: 'cart', onClick: () => navigate('/purchases/new') },
        ].filter(Boolean)
      }
      columns={[
        { header: 'Material', sort: 'name', render: (i) => <Cell title={i.name} sub={[i.itemCode, i.brand, i.specification].filter(Boolean).join(' · ')} />, csv: (i) => i.name },
        { header: 'Code', sort: 'itemCode', render: (i) => <span className="font-mono text-xs">{i.itemCode}</span>, csv: (i) => i.itemCode, defaultHidden: true },
        { header: 'Category', sort: 'category', render: (i) => i.category || <span className="text-ink-300">-</span>, csv: (i) => i.category },
        { header: 'HSN', sort: 'sacHsn', render: (i) => i.sacHsn || '-', csv: (i) => i.sacHsn, defaultHidden: true },
        { header: 'Purchase rate', sort: 'purchaseRate', sortDesc: true, align: 'right', render: (i) => money(i.purchaseRate), csv: (i) => (i.purchaseRate ?? 0) / 100 },
        { header: 'Selling rate', sort: 'sellingRate', sortDesc: true, align: 'right', render: (i) => <>{money(i.sellingRate)}<span className="text-ink-400"> /{i.unit}</span></>, csv: (i) => i.sellingRate / 100 },
        { header: 'GST', sort: 'gstRate', sortDesc: true, align: 'right', render: (i) => `${i.gstRate ?? 0}%`, csv: (i) => i.gstRate, defaultHidden: true },
        { header: 'In stock', sort: 'currentStock', sortDesc: true, align: 'right', render: (i) => <StockQty item={i} />, csv: (i) => i.currentStock },
        { header: 'Min level', align: 'right', render: (i) => `${i.minStockLevel ?? 0}`, csv: (i) => i.minStockLevel, defaultHidden: true },
        { header: 'Stock value', align: 'right', render: (i) => money(Math.round((i.currentStock ?? 0) * (i.avgPurchaseRate ?? 0))), csv: (i) => Math.round((i.currentStock ?? 0) * (i.avgPurchaseRate ?? 0)) / 100 },
        { header: 'Status', sort: 'isActive', render: (i) => (i.isActive ? <Badge tone="green" dot>Active</Badge> : <Badge tone="grey" dot>Inactive</Badge>), csv: (i) => (i.isActive ? 'Active' : 'Inactive'), defaultHidden: true },
      ]}
      mobile={(i) => ({
        title: i.name,
        subtitle: [`${i.currentStock ?? 0} ${i.unit} in stock`, i.category].filter(Boolean).join(' · '),
        right: money(i.sellingRate),
        badge: isLow(i) ? <Badge tone="red">Low</Badge> : null,
      })}
      empty={{ icon: 'box', title: 'No materials yet', message: 'Add the wires, switches and fittings you keep in the shop.', action: add }}
    />
  );
}
